import { fetchWithAuth, getContractList } from './api';

const host = window.location.hostname === 'localhost' ? "http://localhost:8000" : "";

export interface ScenarioReference {
    fileId: number;
    pageNumber: number;
}

export interface ScenarioTool {
    name: string;
    enabled: boolean;
}

export interface Scenario {
    scenario_id: number;
    scenario_name: string;
    question: string;
    group: number;
    sequence: number;
    is_active: boolean;
    retrieval_class: string;
    prompt_name: string | null;
    run_args: any | null;
    created_at: string;
    contract_id: number | null;
    contract_workspace: string | null;
    category?: string | null;
    scope?: string | null;
    source?: Record<string, number[]>;
    references?: ScenarioReference[];
    tools?: ScenarioTool[];
}

export interface ScenarioResponse {
    items: Scenario[];
    total: number;
    page: number;
    limit: number;
}

export const listScenarios = async (page: number = 1, limit: number = 100, contractIds?: number[] | null, filter?: string): Promise<ScenarioResponse> => {
    let url = `${host}/api/scenarios?page=${page}&limit=${limit}`;
    
    if (contractIds && contractIds.length > 0) {
        if (contractIds.length > 1) {
            url += `&filter=CONTRACT`;
        } else {
            url += `&contract_id=${contractIds[0]}`;
            if (filter) {
                url += `&filter=${filter}`;
            } else {
                url += `&filter=INCLUDE_GENERIC`;
            }
        }
    } else if (filter) {
        url += `&filter=${filter}`;
    }
    
    const response = await fetchWithAuth(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    
    const data = await response.json();
    
    if (contractIds && contractIds.length > 1 && data.items) {
        const filteredItems = data.items.filter((scenario: Scenario) => 
            scenario.contract_id !== null && contractIds.includes(scenario.contract_id)
        );
        
        const filterRatio = data.items.length > 0 ? filteredItems.length / data.items.length : 0;
        const estimatedTotal = Math.ceil(data.total * filterRatio);
        
        return {
            ...data,
            items: filteredItems,
            total: estimatedTotal
        };
    }
    
    return data;
};

export const runScenario = async (scenarioId: number, contractIds: string[]): Promise<any> => {
    const response = await fetchWithAuth(`${host}/api/scenarios/run/?scenario_id=${scenarioId}&contract_ids=${contractIds.join(',')}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response.json();
};

export const generateScenarios = async (contractIds: string[]): Promise<any> => {
    const response = await fetchWithAuth(`${host}/api/scenarios/generate/?contract_ids=${contractIds.join(',')}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response.json();
};

export const getScenario = async (scenarioId: number): Promise<Scenario> => {
    const response = await fetchWithAuth(`${host}/api/scenarios/${scenarioId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response.json();
};

export const deleteScenario = async (scenarioId: number): Promise<void> => {
    await fetchWithAuth(`${host}/api/scenarios/${scenarioId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
    });
};

export const createScenario = async (scenario: Partial<Scenario>): Promise<Scenario> => {
    const response = await fetchWithAuth(`${host}/api/scenarios`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(scenario),
    });
    return response.json();
};

export const updateScenario = async (scenarioId: number, scenario: Partial<Scenario>): Promise<Scenario> => {
    const response = await fetchWithAuth(`${host}/api/scenarios/${scenarioId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(scenario),
    });
    return response.json();
};

export const getContractWorkspaces = async (): Promise<Array<{id: string, label: string}>> => {
    const response = await getContractList('', 'name');
    if (response?.data?.contracts) {
        // Filter to only include ANALYSED contracts and format for dropdown
        return response.data.contracts
            .filter((contract: any) => contract.status === 'ANALYSED')
            .map((contract: any) => ({
                id: contract.contract_id.toString(),
                label: contract.contract_workspace || 'Unnamed Workspace'
            }));
    }
    return [];
};

// New function to fetch value pools
export const getValuePools = async (): Promise<string[]> => {
    const response = await fetchWithAuth(`${host}/api/scenarios/value-pools`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response.json();
};

// New function to fetch scenario scopes
export const getScenarioScopes = async (): Promise<string[]> => {
    const response = await fetchWithAuth(`${host}/api/scenarios/scopes`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
    return response.json();
};
